import { type PropsWithChildren, forwardRef, useMemo } from 'react';
import uniqueId from 'lodash/uniqueId';
import { IconButton } from '../../buttons';
import { useTheme } from '../../hooks';
import { XIcon } from '../../icons/phosphor';
import { styled } from '../../theme';
import { Slide, type TransitionProps } from '../../transitions';
import { AppBar, type AppBarProps } from '../AppBar';
import { Box } from '../Box';
import { Dialog } from '../Dialog';
import { ScrollBox } from '../ScrollBox';
import { Toolbar } from '../Toolbar';
import { FullScreenOverlayTitle } from './FullScreenOverlayTitle';

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        borderRadius: 0,
        overflow: 'hidden',
    },
});

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export interface Props extends PropsWithChildren<unknown> {
    headerContent?: React.ReactNode;
    toolbarActions?: React.ReactNode;
    title?: React.ReactNode;
    titleCentered?: boolean;
    open: boolean;
    onClose?: () => void;
    ['aria-labelledby']?: string;
    hideDefaultCloseButton?: boolean;
    appBarProps?: AppBarProps;
}

export const FullScreenOverlay = ({
    children,
    headerContent,
    toolbarActions,
    open,
    title,
    titleCentered,
    onClose,
    hideDefaultCloseButton = false,
    appBarProps,
    ...props
}: Props) => {
    const theme = useTheme();
    const a11y = useMemo(() => generateA11y(), []);

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            fullScreen
            TransitionComponent={Transition}
            aria-labelledby={typeof title === 'string' ? a11y.titleId : props['aria-labelledby']}
        >
            <AppBar
                elevation={1}
                sx={{
                    position: 'static',
                    backgroundColor: (theme) => theme.colors.background.default,
                    color: 'text.primary',
                    zIndex: (theme) => theme.zIndex.modal,
                }}
                {...appBarProps}
            >
                <StyledToolbar>
                    {!hideDefaultCloseButton && (
                        <TitleButtonBox ml={-1.5} mr={{ xs: 1, sm: 2 }}>
                            <IconButton onClick={onClose} aria-label="Close">
                                <XIcon size={32} color={theme.palette.text.secondary} />
                            </IconButton>
                        </TitleButtonBox>
                    )}
                    {!!title && (
                        <>
                            <Box sx={{ flex: 1, minWidth: 0, mt: 0.5 }}>
                                {typeof title === 'string' ? (
                                    <FullScreenOverlayTitle
                                        id={a11y.titleId}
                                        textAlign={titleCentered ? 'center' : 'start'}
                                    >
                                        {title}
                                    </FullScreenOverlayTitle>
                                ) : (
                                    title
                                )}
                            </Box>
                            {titleCentered && <TitleButtonBox ml={{ xs: 1, sm: 2 }} mr={-1.5} />}
                        </>
                    )}
                    {toolbarActions}
                </StyledToolbar>
                {headerContent}
            </AppBar>
            <ScrollBox>{children}</ScrollBox>
        </StyledDialog>
    );
};

const TitleButtonBox = styled(Box)({
    width: 56,
    height: 56,
});

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
}));

const generateA11y = () => {
    return {
        titleId: uniqueId('overlay-title-'),
    };
};
